import { Link, Navigate, useNavigate } from "react-router";
import { useAuth } from "../auth/AuthProvider";
import { ErrorMessage, Loading } from "../components/StatusMessage";
import { formatUsd } from "../../shared/money";
import { useI18n } from "../i18n/I18nProvider";
import { api } from "../lib/api";
import { useAsync } from "../lib/useAsync";
import { CollectionSection } from "./account/CollectionSection";
import { ProfileForm } from "./account/ProfileForm";
import { WatchesList } from "./account/WatchesList";
import { PushSettings } from "./account/PushSettings";
import { marketApi } from "../market/api";
import { CountBadge, useInbox } from "../market/InboxProvider";

/**
 * Mi cuenta's one way to the shop: without one, the invitation to open it; with one, its name, what
 * it has to do today and the way in (the shop itself lives in /mi-tienda).
 */
function ShopCard() {
  const { t } = useI18n();
  const seller = useAsync((signal) => marketApi.mySeller(signal), []);
  const open = seller.data?.status === "active" || seller.data?.status === "paused";
  const tasks = useAsync((signal) => (open ? marketApi.tasks(signal) : Promise.resolve(null)), [open]);
  if (seller.loading || seller.error) return null;
  const s = seller.data;
  if (!s || s.status === "rejected")
    return (
      <section className="plan-card account-shop" aria-label={t("mkt.myShop")}>
        <p>
          <strong>🏪 {t("acct.shopNone")}</strong>{" "}
          <Link to="/vender" className="btn btn-outline btn-sm">
            {t("acct.shopOpen")}
          </Link>
        </p>
      </section>
    );
  const n = tasks.data?.length ?? 0;
  return (
    <section className="plan-card account-shop" aria-label={t("mkt.myShop")}>
      <p>
        <strong>🏪 {s.displayName}</strong>
        {s.status === "pending" ? ` · ${t("acct.shopPending")}` : tasks.data ? ` · ${n > 0 ? t("acct.shopTodo", { count: n }) : t("acct.shopAllDone")}` : ""}{" "}
        <Link to="/mi-tienda" className="btn btn-primary btn-sm">
          {t("acct.shopGo")}
        </Link>
      </p>
    </section>
  );
}

export function AccountPage() {
  const { t, locale } = useI18n();
  const { inbox } = useInbox();
  const formatPrice = (cents: number) => formatUsd(cents, locale);
  const auth = useAuth();
  const navigate = useNavigate();
  const orders = useAsync((signal) => (auth.user ? api.myOrders(signal) : Promise.resolve([])), [auth.user?.id]);

  if (auth.loading) return <Loading />;
  if (!auth.user) return <Navigate to="/ingresar?next=%2Fcuenta" replace />;

  return (
    <div className="container section account">
      <div className="section-head">
        <div>
          <h1>{t("account.title")}</h1>
          <p className="muted">
            {t("account.hello", { name: auth.user.name })} · {auth.user.email}
          </p>
        </div>
        <button
          type="button"
          className="btn btn-outline"
          onClick={async () => {
            await auth.signOut();
            navigate("/", { replace: true });
          }}
        >
          {t("auth.logout")}
        </button>
      </div>

      <ShopCard />

      <nav className="account-market" aria-label={t("mkt.title")}>
        <Link to="/cuenta/compras" className="account-tile">
          <strong>
            🛒 {t("mkt.myMarket")} <CountBadge count={inbox.purchases + inbox.buyerOffers} />
          </strong>
          <span className="muted small">{t("mkt.myMarketHint")}</span>
        </Link>
        <Link to="/cuenta/compramos" className="account-tile">
          <strong>🃏 {t("buy.mine")}</strong>
          <span className="muted small">{t("buy.mineHint")}</span>
        </Link>
      </nav>

      <PushSettings />
      <CollectionSection />
      <ProfileForm />
      <WatchesList />

      <h2 className="section-title">{t("account.orders")}</h2>
      {orders.error ? (
        <ErrorMessage onRetry={orders.reload} />
      ) : orders.loading || !orders.data ? (
        <Loading />
      ) : orders.data.length === 0 ? (
        <div className="status">
          <p>{t("account.noOrders")}</p>
          <Link to="/mercado" className="btn btn-primary">
            {t("cart.continue")}
          </Link>
        </div>
      ) : (
        <ul className="order-list">
          {orders.data.map((o) => (
            <li key={o.orderNumber} className="order-row">
              <div>
                <strong>{o.orderNumber}</strong>
                <span className="muted small">{new Date(o.createdAt).toLocaleDateString(locale === "es" ? "es-AR" : "en-US")}</span>
              </div>
              <span className={`pill pill-${o.paymentStatus}`}>{t(`order.status.${o.paymentStatus}`)}</span>
              <strong>{formatPrice(o.total)}</strong>
              <Link to={`/pedido/${o.orderNumber}`} className="btn btn-link">
                {t("account.viewOrder")}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
